import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { inventoryCategoriesService, InventoryCategory, InventoryCategoryCreateEntry } from '../services/inventoryCategoriesService';

// shadcn/ui components
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { AlertTriangle } from 'lucide-react';

interface InventoryCategoryFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (category: InventoryCategory) => void;
  category?: InventoryCategory | null;
}

export const ModernInventoryCategoryForm: React.FC<InventoryCategoryFormProps> = ({
  open,
  onClose,
  onSave,
  category,
}) => {
  const { translate } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<InventoryCategoryCreateEntry>({
    name: '',
    description: '',
    isPackageRequired: false,
    isActive: true,
  });

  useEffect(() => {
    if (open) {
      if (category) {
        setFormData({
          name: category.name,
          description: category.description || '',
          isPackageRequired: category.isPackageRequired,
          isActive: category.isActive,
        });
      } else {
        setFormData({
          name: '',
          description: '',
          isPackageRequired: false,
          isActive: true,
        });
      }
      setError(null);
    }
  }, [open, category]);

  const handleInputChange = (
    field: keyof InventoryCategoryCreateEntry,
    value: any
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.name) {
        setError(translate('pos.inventoryCategories.form.required', 'Please fill in all required fields'));
        setLoading(false);
        return;
      }

      let savedCategory: InventoryCategory;
      if (category) {
        savedCategory = await inventoryCategoriesService.updateInventoryCategory(
          category.id,
          formData
        );
      } else {
        savedCategory = await inventoryCategoriesService.createInventoryCategory(formData);
      }

      onSave(savedCategory);
    } catch (err) {
      console.error('Failed to save inventory category:', err);
      setError(
        err instanceof Error
          ? err.message
          : translate('pos.inventoryCategories.form.error', 'Failed to save category')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {category
              ? translate('pos.inventoryCategories.edit', 'Edit Category')
              : translate('pos.inventoryCategories.create', 'Create Category')}
          </DialogTitle>
          <DialogDescription>
            {category
              ? translate('pos.inventoryCategories.form.editDescription', 'Update category information')
              : translate('pos.inventoryCategories.form.createDescription', 'Create a new inventory category')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle>
                {translate('pos.inventoryCategories.form.basicInfo', 'Basic Information')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  {translate('pos.inventoryCategories.form.name', 'Name')} *
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">
                  {translate('pos.inventoryCategories.form.description', 'Description')}
                </Label>
                <Textarea
                  id="description"
                  value={formData.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="isPackageRequired"
                  checked={formData.isPackageRequired}
                  onCheckedChange={(checked) =>
                    handleInputChange('isPackageRequired', checked)
                  }
                />
                <Label htmlFor="isPackageRequired">
                  {translate('pos.inventoryCategories.form.isPackageRequired', 'Package Required')}
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) =>
                    handleInputChange('isActive', checked)
                  }
                />
                <Label htmlFor="isActive">
                  {translate('pos.inventoryCategories.form.isActive', 'Active')}
                </Label>
              </div>
            </CardContent>
          </Card>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              {translate('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading
                ? translate('common.saving', 'Saving...')
                : translate('common.save', 'Save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

